/**
 * Step1 / Step2 の音声操作 (Audio プレイヤー / 変更内容でAudio再作成 / ダウンロード)。
 *
 * SBI-002: Step1 の放送テキストエリアの直下に置き、入力中のプレーンテキストを
 * そのまま音声にして確認する。
 * SBI-005: Step2 の辞書適用済 SSML 編集欄の直下にも同じ部品を置き、
 * 編集した SSML で音声を作り直して確認する (props で差分だけを切り替える)。
 * SBI-009: 描画は Step3 とも共通の `AudioControls` に移した。本コンポーネントは
 * 「音声を 1 件だけ持つ状態管理」(`useSsmlAudio`) と「再作成できる条件」を担う
 * (旧 `Step1AudioControls`。Step1 専用ではなくなったため改名した)。
 *
 * 次の 3 つを置く。
 *
 *   - Audio プレイヤー          … 作成済みの音声を試聴する
 *   - 変更内容でAudio再作成     … 現在の入力 (放送テキスト / 編集した SSML) で音声を (作り直して) 作る
 *   - ダウンロード              … 再生している音声と **同じ Blob URL** を保存する
 *
 * ## 設計上の約束
 *
 * - **入力をそのまま Polly へ連携する**。SSML 辞書は再適用しない
 *   (`previewAudio` が `applyDictionary: false` で呼ぶ。Step2 の SSML は適用済みなので二重適用を防ぐ)。
 *   - Step1 (既定): プレーンテキスト。`<speak>` で囲むのと `& < >` のエスケープだけ (`buildPreviewSsml`)。
 *   - Step2: 編集した SSML。`<speak>` の補完と空・長さの検査だけ (`prepareEditedSsml`)。
 * - **自動生成しない**。入力のたびに合成すると Polly 課金が発生し続けるため、
 *   利用者が「変更内容でAudio再作成」を押したときだけ合成する。
 * - 合成・キャッシュ・blob URL の解放は Step3 と同じ `useSsmlAudio` に任せる
 *   (解放漏れを 1 箇所に閉じる)。本コンポーネントは描画と「どの文面から作った音声か」の管理だけ。
 * - **ダウンロードで `/audio-preview` を呼び直さない**。再生と同じ URL を `<a download>` に渡す
 *   (聴いた音と保存した音がずれず、Polly の二重課金も無い)。
 * - 入力を編集したのに再作成していない間は「編集内容は音声に反映されていません」を出す
 *   (編集前の音声を聴き続ける誤解を防ぐ)。
 */
import { useState } from 'react';
import type React from 'react';

import { AudioControls, RECREATE_AUDIO_BUTTON_LABEL } from './AudioControls';
import {
  buildStepAudioTestIds,
  getReadyAudioUrl,
  hasEditedSinceApplied,
  isAudioSynthesizing,
} from './audioControlsUtils';
import { buildAudioFileName } from './fileName';
import { buildPreviewSsml } from './ssmlDraft';
import type { PrepareEditedSsmlResult } from './ssmlDraft';
import { useSsmlAudio } from './useSsmlAudio';
import type { PreviewAudioFn } from './useSsmlAudio';

/** 音声状態を `useSsmlAudio` に登録するキー (Step1 は 1 件だけ)。 */
const AUDIO_KEY = 'step1';

/** ダウンロード名の後半 (`遅延案内_プレーンテキスト.mp3`)。Step3 の候補タイトルに相当する。 */
export const STEP1_AUDIO_TITLE = 'プレーンテキスト';

/** Step2 のダウンロード名の後半 (`遅延案内_辞書適用.mp3`)。SBI-005。 */
export const STEP2_AUDIO_TITLE = '辞書適用';

/** 再作成ボタンの名称 (全 Step 共通の文言 / SBI-002)。定義は `AudioControls` に移した。 */
export { RECREATE_AUDIO_BUTTON_LABEL };

/** testid の接頭辞の既定値 (Step1)。 */
const DEFAULT_TEST_ID_PREFIX = 'ssml-tuning-step1';

export interface StepAudioControlsProps {
  /**
   * 音声にする入力 (親が保持する値)。
   * Step1 は放送テキスト、Step2 は編集中の辞書適用済 SSML。
   */
  text: string;
  /** 音声合成関数 (`POST /audio-preview` / Polly Takumi 固定)。 */
  previewAudio: PreviewAudioFn;
  /**
   * data-testid の接頭辞 (既定 `ssml-tuning-step1`)。
   * `${prefix}-audio` / `-audio-player` / `-audio-status` / `-recreate` / `-download` /
   * `-dirty` / `-prepare-error` / `-audio-error` を作る。同じ画面に 2 つ置くため Step ごとに分ける。
   */
  testIdPrefix?: string;
  /** ダウンロード名の後半 (既定 {@link STEP1_AUDIO_TITLE})。 */
  audioTitle?: string;
  /**
   * ダウンロード名の前半 (放送タイトル) の元にする放送テキスト。
   * 既定は `text`。Step2 は `text` が SSML なので、Step1 の放送テキスト (原文) を渡す。
   */
  fileNameText?: string;
  /**
   * 入力を `/audio-preview` へ送れる SSML に整える関数 (既定 `buildPreviewSsml` = プレーンテキスト用)。
   * Step2 は SSML なので `prepareEditedSsml` を渡す。
   */
  prepareSsml?: (text: string) => PrepareEditedSsmlResult;
  /** 入力が空のとき、再作成ボタンの title に出す案内 (既定は放送テキスト向け)。 */
  emptyReason?: string;
}

export function StepAudioControls({
  text,
  previewAudio,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
  audioTitle = STEP1_AUDIO_TITLE,
  fileNameText,
  prepareSsml = buildPreviewSsml,
  emptyReason = '放送テキストを入力すると音声を作成できます。',
}: StepAudioControlsProps): React.JSX.Element {
  const { audioStates, synthesize } = useSsmlAudio(previewAudio);
  const audio = audioStates[AUDIO_KEY];
  /**
   * 現在の音声が **どの放送テキストから作られたか**。
   * 合成に成功したときだけ更新する (失敗時に更新すると「反映済み」と誤表示される)。
   */
  const [appliedText, setAppliedText] = useState<string | null>(null);
  /** 合成の前段で弾いた理由 (空 / 長すぎ)。合成そのものの失敗は `audio.error` に出る。 */
  const [prepareError, setPrepareError] = useState<string | null>(null);

  const isSynthesizing = isAudioSynthesizing(audio);
  const hasText = text.trim() !== '';
  const hasAudio = getReadyAudioUrl(audio) !== null;
  const isAudioError = audio?.status === 'error';
  /*
   * 編集済みだが再作成していない状態。
   * ⚠️ 前後の空白差は「編集」と見なさない (Step3 と同じ規則)。
   */
  const isDirty = hasAudio && hasEditedSinceApplied(text, appliedText);

  /*
   * 「変更内容でAudio再作成」を押せる条件:
   *   - 放送テキストがある (空では合成できない)
   *   - 合成中でない (二重の Polly 呼び出しを防ぐ)
   *   - 音声が無い / 失敗している / 編集済み (未編集の再合成は課金の無駄)
   */
  const needsSynthesis = !hasAudio || isAudioError || isDirty;
  const recreateDisabled = !hasText || isSynthesizing || !needsSynthesis;
  const recreateTitle = ((): string | undefined => {
    if (!hasText) {
      return emptyReason;
    }
    if (isSynthesizing) {
      return '音声の生成が終わると再作成できます。';
    }
    if (!needsSynthesis) {
      return '内容を編集すると再作成できます。';
    }
    return undefined;
  })();

  async function handleRecreate(): Promise<void> {
    const prepared = prepareSsml(text);
    if (!prepared.ok) {
      setPrepareError(prepared.error);
      return;
    }
    setPrepareError(null);
    const ok = await synthesize(AUDIO_KEY, prepared.ssml);
    if (ok) {
      setAppliedText(text);
    }
  }

  const downloadFileName = buildAudioFileName({
    broadcastText: fileNameText ?? text,
    candidateTitle: audioTitle,
  });

  return (
    <AudioControls
      audio={audio}
      isDirty={isDirty}
      testIds={buildStepAudioTestIds(testIdPrefix)}
      recreate={{
        onClick: () => void handleRecreate(),
        disabled: recreateDisabled,
        title: recreateTitle,
      }}
      download={{ fileName: downloadFileName }}
    >
      {/* 合成の前段で弾いた理由 (空 / 長すぎ)。合成そのものの失敗は共通部品が出す。 */}
      {prepareError !== null && (
        <p className="ssml-tuning-error" role="alert" data-testid={`${testIdPrefix}-prepare-error`}>
          {prepareError}
        </p>
      )}
    </AudioControls>
  );
}
